// NASA Satellite Data Service
// Enhances existing satellite tracking with NASA mission data

const NASA_API_KEY = process.env.REACT_APP_NASA_API_KEY || 'DEMO_KEY';

class NasaSatelliteService {
  constructor() {
    this.nasaData = new Map(); // Cache NASA satellite data by NORAD ID
    this.lastFetch = null;
    this.cacheDuration = 24 * 60 * 60 * 1000; // 24 hours
  }

  /**
   * Fetch NASA satellite data and cache it
   * @returns {Promise<Map>} Map of NORAD ID to NASA satellite data
   */
  async fetchNasaSatelliteData() {
    // Check if we have recent cached data
    if (this.lastFetch && (Date.now() - this.lastFetch) < this.cacheDuration) {
      console.log('✅ Using cached NASA satellite data');
      return this.nasaData;
    }

    try {
      console.log('🌌 Fetching NASA satellite data via Firebase Function...');

      // Use Firebase Function to proxy NASA API calls (avoids CORS issues)
      const firebaseFunctionUrl = `https://getnasasatellitedata-3ogooxwlda-uc.a.run.app`;

      const response = await fetch(firebaseFunctionUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.warn('Firebase Function unavailable, using fallback data');
        return this.getFallbackNasaData();
      }

      const data = await response.json();

      if (data.success && data.satellites) {
        console.log(`✅ Received ${data.satellites.length} NASA satellites from Firebase Function`);

        // Process and cache the data
        this.nasaData.clear(); // Clear existing data
        data.satellites.forEach(satellite => {
          this.nasaData.set(satellite.noradId, satellite);
        });

        this.lastFetch = Date.now();
        console.log(`✅ Processed ${this.nasaData.size} NASA satellite records`);
        return this.nasaData;
      } else {
        console.warn('Invalid response from Firebase Function, using fallback data');
        return this.getFallbackNasaData();
      }

    } catch (error) {
      console.error('❌ Error fetching NASA satellite data:', error);
      return this.getFallbackNasaData();
    }
  }

  /**
   * Process NASA API data and create NORAD ID mappings
   * @param {Object} nasaData - Raw NASA API response
   */
  processNasaData(nasaData) {
    if (!nasaData.projects) return;

    nasaData.projects.forEach(project => {
      // Extract satellite information from NASA projects
      if (project.hardware && project.hardware.length > 0) {
        project.hardware.forEach(hardware => {
          if (hardware.noradId) {
            const noradId = hardware.noradId.toString();

            this.nasaData.set(noradId, {
              name: hardware.name || project.title,
              mission: project.title,
              description: project.description,
              status: project.status,
              startDate: project.startDate,
              endDate: project.endDate,
              leadOrganization: project.leadOrganization,
              program: project.program,
              instruments: hardware.instruments || [],
              objectives: project.objectives || [],
              website: project.website,
              lastUpdated: new Date().toISOString(),
              source: 'NASA TechPort'
            });
          }
        });
      }
    });
  }

  /**
   * Get fallback NASA data for common satellites
   * @returns {Map} Fallback NASA satellite data
   */
  getFallbackNasaData() {
    console.log('🔄 Using fallback NASA satellite data');

    const fallbackData = new Map([
      // ISS
      ['25544', {
        name: 'International Space Station',
        mission: 'International Space Station Program',
        description: 'Orbital laboratory for scientific research and international cooperation',
        status: 'Operational',
        startDate: '1998-11-20',
        leadOrganization: 'NASA',
        program: 'Human Spaceflight',
        instruments: ['Microgravity Research Facilities', 'Earth Observation Cameras', 'Communications Systems'],
        objectives: ['Scientific Research', 'Technology Development', 'International Cooperation'],
        website: 'https://www.nasa.gov/mission_pages/station/main/index.html',
        lastUpdated: new Date().toISOString(),
        source: 'NASA Fallback Data'
      }],

      // Hubble Space Telescope
      ['20580', {
        name: 'Hubble Space Telescope',
        mission: 'Hubble Space Telescope',
        description: 'Space-based optical telescope for astronomical observations',
        status: 'Operational',
        startDate: '1990-04-24',
        leadOrganization: 'NASA',
        program: 'Astronomy',
        instruments: ['Wide Field Camera 3', 'Cosmic Origins Spectrograph', 'Space Telescope Imaging Spectrograph'],
        objectives: ['Astronomical Research', 'Deep Space Imaging', 'Spectroscopic Analysis'],
        website: 'https://www.nasa.gov/mission_pages/hubble/main/index.html',
        lastUpdated: new Date().toISOString(),
        source: 'NASA Fallback Data'
      }],

      // GPS Satellites (example)
      ['32260', {
        name: 'GPS IIF-1',
        mission: 'Global Positioning System',
        description: 'Satellite navigation system providing positioning and timing services',
        status: 'Operational',
        startDate: '2010-05-27',
        leadOrganization: 'US Air Force',
        program: 'Navigation',
        instruments: ['GPS Transmitters', 'Atomic Clocks', 'Solar Panels'],
        objectives: ['Global Navigation', 'Timing Services', 'Search and Rescue'],
        website: 'https://www.gps.gov/',
        lastUpdated: new Date().toISOString(),
        source: 'NASA Fallback Data'
      }]
    ]);

    // Cache the fallback data
    this.nasaData = fallbackData;
    return fallbackData;
  }

  /**
   * Get NASA data for a specific satellite by NORAD ID
   * @param {string} noradId - NORAD ID of the satellite
   * @returns {Object|null} NASA satellite data or null if not found
   */
  getSatelliteData(noradId) {
    return this.nasaData.get(noradId.toString()) || null;
  }

  /**
   * Enhance satellite data with NASA information
   * @param {Array} satellites - Array of satellite objects
   * @returns {Array} Enhanced satellites with NASA data
   */
  async enhanceSatellites(satellites) {
    // Ensure we have NASA data
    if (this.nasaData.size === 0) {
      await this.fetchNasaSatelliteData();
    }

    return satellites.map(satellite => {
      // Extract NORAD ID from TLE data
      const noradId = this.extractNoradId(satellite.tle);
      const nasaData = noradId ? this.getSatelliteData(noradId) : null;

      return {
        ...satellite,
        nasaData: nasaData,
        noradId: noradId
      };
    });
  }

  /**
   * Extract NORAD ID from TLE data
   * @param {Array} tle - Two-line element data
   * @returns {string|null} NORAD ID or null if not found
   */
  extractNoradId(tle) {
    if (!tle || !tle[0]) return null;

    try {
      // NORAD ID is in the first line of TLE, positions 3-7
      const line1 = tle[0];
      if (line1.length >= 7) {
        const noradId = line1.substring(2, 7).trim();
        return noradId;
      }
    } catch (error) {
      console.warn('Error extracting NORAD ID from TLE:', error);
    }

    return null;
  }

  /**
   * Get status color for NASA satellite status
   * @param {string} status - NASA status string
   * @returns {string} Color code
   */
  getStatusColor(status) {
    if (!status) return '#666666';

    const statusLower = status.toLowerCase();

    if (statusLower.includes('operational') || statusLower.includes('active')) {
      return '#00ff00'; // Green
    }
    if (statusLower.includes('inactive') || statusLower.includes('decommissioned')) {
      return '#ff0000'; // Red
    }
    if (statusLower.includes('planned') || statusLower.includes('development')) {
      return '#ffff00'; // Yellow
    }

    return '#ffffff'; // White (unknown)
  }
}

// Export singleton instance
export const nasaSatelliteService = new NasaSatelliteService();
